import { openNotification } from '@/common/helpers';
import { NotificationTypes } from '@/common/interfaces';
import { useTypedSelector } from '@/hooks/useTypedSelector';
import { downloadFile } from '@/pages/Registration/Documents/Documents.helpers';
import { ordersApi } from '@/store/api/orders.api';
import { userApi } from '@/store/api/user.api';
import { setFilters } from '@/store/reducers/orders.slice';
import {
  BarChartOutlined,
  PlusCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, Divider, Pagination, Table } from 'antd';
import React, { FC, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Filters } from './Filters';
import { ModalActiveOrder } from './ModalActiveOrder';
import { ModalCreateOrder } from './ModalCreateOrder';
import { ModalReport } from './ModalReport';
import { getOrderColumns } from './Orders.helpers';
import { Order } from './Orders.interfaces';
import styles from './Orders.module.scss';

export const Orders: FC = () => {
  const { currency, virtualAssets } = useTypedSelector(
    (state) => state.registration,
  );

  const { filters } = useTypedSelector((state) => state.orders);
  const { isMobile } = useTypedSelector((state) => state.common);

  const [isOpenModalCreateOrder, setIsOpenModalCreateOrder] = useState(false);
  const [isOpenModalActiveOrder, setIsOpenModalActiveOrder] = useState(false);
  const [isOpenModalReport, setIsOpenModalReport] = useState(false);
  const [activeOrder, setActiveOrder] = useState<null | Order>(null);

  const [dataOrders, setDataOrders] = useState<{
    orders: Order[];
    quantity: number;
  }>({ orders: [], quantity: 0 });

  // Получение списка заявок
  const [getOrders, { isLoading: isLoadingOrders }] =
    ordersApi.useGetOrdersMutation();

  // Получение файла по ID
  const [getFileBase64, { isLoading: isLoadingDownload }] =
    userApi.useGetFileBase64Mutation();

  const dispatch = useDispatch();

  const handleOpenModalCreateOrder = () => {
    setIsOpenModalCreateOrder(true);
  };

  const handleCloseModalCreateOrder = () => {
    setIsOpenModalCreateOrder(false);
  };

  const handleOpenModalActiveOrder = (activeData: Order) => {
    // Если выделяем текст, onClick не сработает
    const selection = window.getSelection()?.toString();
    if (selection) {
      return;
    }
    setIsOpenModalActiveOrder(true);
    setActiveOrder(activeData);
  };

  const handleCloseModalActiveOrder = () => {
    setIsOpenModalActiveOrder(false);
    setActiveOrder(null);
  };

  const handleOpenModalReport = () => {
    setIsOpenModalReport(true);
  };

  const handleCloseModalReport = () => {
    setIsOpenModalReport(false);
  };

  const onChangePage = (page: number, pageSize: number) => {
    const updateFilters = {
      ...filters,
      offset: page === 1 ? 0 : (page - 1) * pageSize,
    };
    dispatch(setFilters(updateFilters));
    getOrders(updateFilters);
  };

  const onGetOrders = React.useCallback(async () => {
    try {
      const res = await getOrders(filters).unwrap();
      setDataOrders(res);
    } catch (e) {
      openNotification(
        NotificationTypes.ERROR,
        `Произошла ошибка при загрузке списка заявок: ${e}`,
      );
    }
  }, [filters, getOrders]);

  const handleDownloadPDF = async (orderId: string) => {
    try {
      const res = await getFileBase64({
        fileId: orderId,
      }).unwrap();

      if (res.data) {
        downloadFile(res);
        openNotification(NotificationTypes.SUCCESS, 'Файл успешно загружен.');
      }
    } catch (e) {
      openNotification(
        NotificationTypes.ERROR,
        `Ошибка при загрузке файла: ${e}`,
      );
    }
  };

  const columns = getOrderColumns(currency, virtualAssets, handleDownloadPDF);

  React.useEffect(() => {
    onGetOrders();
  }, [onGetOrders]);

  return (
    <div>
      <h1 className={styles.title}>Заявки</h1>

      <div className={styles.buttons}>
        <Button
          style={isMobile ? { width: 'auto' } : { width: '130px' }}
          size="large"
          icon={<SearchOutlined />}
          htmlType="submit"
          color="primary"
          variant="outlined"
          onClick={onGetOrders}
        >
          Поиск
        </Button>

        {isMobile ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 12,
              width: '100%',
            }}
          >
            <Button
              onClick={handleOpenModalCreateOrder}
              icon={<PlusCircleOutlined />}
              size="large"
              type="primary"
              style={{ flex: 1, minWidth: 0 }}
            />
            <Button
              className={styles['buttons__right']}
              onClick={handleOpenModalReport}
              icon={<BarChartOutlined />}
              size="large"
              color="danger"
              variant="dashed"
              style={{ flex: 1, minWidth: 0 }}
            />
          </div>
        ) : (
          <>
            <Button
              onClick={handleOpenModalCreateOrder}
              icon={<PlusCircleOutlined />}
              size="large"
              type="primary"
            >
              Новая заявка
            </Button>

            <Button
              className={styles['buttons__right']}
              onClick={handleOpenModalReport}
              icon={<BarChartOutlined />}
              size="large"
              color="danger"
              variant="dashed"
            >
              Сформировать отчет
            </Button>
          </>
        )}
      </div>

      <Divider style={{ margin: '15px 0' }} />

      <Filters filters={filters} />

      <Table
        className="custom-scroll"
        scroll={{ x: 'max-content' }}
        loading={isLoadingOrders}
        dataSource={dataOrders.orders ?? []}
        columns={columns}
        pagination={false}
        rowClassName={styles.row}
        onRow={(record: Order) => ({
          onClick: () => handleOpenModalActiveOrder(record), // Добавляем обработчик клика
        })}
      />

      <div className={styles.pagination}>
        <Pagination
          showSizeChanger={false}
          onChange={onChangePage}
          current={
            filters.offset && filters.limit
              ? Math.floor(filters.offset / filters.limit) + 1
              : 0
          }
          total={dataOrders.quantity}
          pageSize={filters.limit}
        />
      </div>

      <ModalCreateOrder
        onGetOrders={onGetOrders}
        isOpen={isOpenModalCreateOrder}
        onClose={handleCloseModalCreateOrder}
      />

      <ModalActiveOrder
        onGetOrders={onGetOrders}
        data={activeOrder}
        isOpen={isOpenModalActiveOrder}
        onClose={handleCloseModalActiveOrder}
      />

      <ModalReport
        onClose={handleCloseModalReport}
        isOpen={isOpenModalReport}
      />
    </div>
  );
};
